import { t } from "i18next";
import { DataTableData, DataTableDataCellValue } from "../../../../../components/deprecated/DataTable/DataTable";
import { PropertyData } from "../../../Property/PropertyData";
import { PropertyDataFloorsField, PropertyDataJointCostField } from "../../DataObjectField/CustomFields/PropertyFields";
import { DataObjectHelper } from "../../DataObjects/DataObjectHelper";
import { FirebaseInfoField } from "../../FirebaseInfoField";
import { LocalData } from "../../LocalData";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ExpirationProfileWidgetHelper } from "@pages/Dashboard/Widgets/ExpirationProfileWidget/ExpirationProfileWidgetHelper";
import { Popup } from "../../../../../components/deprecated/Popup/Popup";
import { FirebaseDataCompanyField } from "../../DataObjectField/CustomFields/FirebaseDataCompanyField";
import { FirebaseDataContractorField } from "../../DataObjectField/CustomFields/FirebaseDataContractorField";
import { FirebaseDataLocationField } from "../../DataObjectField/CustomFields/FirebaseDataLocationField";
import { FirebaseDataUserField } from "../../DataObjectField/CustomFields/FirebaseDataUserField";
import { FirebaseDataDateField } from "../../DataObjectField/Generic/FirebaseDataDateField";
import { FirebaseDataNumberField } from "../../DataObjectField/Generic/FirebaseDataNumberField";
import { FirebaseDataStringField } from "../../DataObjectField/Generic/FirebaseDataStringField";
import { ToastUtilities } from "@libs/react-toastify/ToastUtilities";

export class PropertyDataHelper extends DataObjectHelper<PropertyData> {
  id = () => "property";
  displayName = () => t("main.property");
  displayNamePlural = () => t("main.properties");

  name = new FirebaseDataStringField<PropertyData>("name", t("main.name"), (data) => data.name, "title");
  companies = new FirebaseDataCompanyField<PropertyData>("companies", t("main.companies"), (data) => data.companies, "business", true);
  contractorGroups = new FirebaseDataContractorField<PropertyData>(
    "contractorGroups",
    t("main.contractors"),
    (data) => data.contractors,
    "label",
    true
  );

  location = new FirebaseDataLocationField<PropertyData>("location", t("main.location"), (data) => data.location?.value, "place");
  address = new FirebaseDataStringField<PropertyData>("address", t("main.address"), (data) => data.address?.value, "place");
  visitorAddress = new FirebaseDataStringField<PropertyData>(
    "visitorAddress",
    t("main.visitorAddress"),
    (data) => data.visitorAddress?.value,
    "place"
  );
  postalAddress = new FirebaseDataStringField<PropertyData>("postalAddress", t("main.postalAddress"), (data) => data.postalAddress?.value, "place");

  city = new FirebaseDataStringField<PropertyData>("city", t("main.city"), (data) => data.city?.value, "place");
  municipality = new FirebaseDataStringField<PropertyData>("municipality", t("main.municipality"), (data) => data.municipality?.value, "place");
  municipalityNumber = new FirebaseDataStringField<PropertyData>(
    "municipalityNumber",
    t("main.municipalityNumber"),
    (data) => data.municipalityNumber?.value,
    "place"
  );

  organizationName = new FirebaseDataStringField<PropertyData>(
    "organizationName",
    t("main.organizationName"),
    (data) => data.organizationName?.value,
    "place"
  );
  organizationNumber = new FirebaseDataStringField<PropertyData>(
    "organizationNumber",
    t("main.organizationNumber"),
    (data) => data.organizationNumber?.value,
    "place"
  );

  gnr = new FirebaseDataStringField<PropertyData>("gnr", t("main.gnr"), (data) => data.gnr?.value, "place");
  bnr = new FirebaseDataStringField<PropertyData>("bnr", t("main.bnr"), (data) => data.bnr?.value, "place");
  snr = new FirebaseDataStringField<PropertyData>("snr", t("main.snr"), (data) => data.snr?.value, "place");
  floors = new PropertyDataFloorsField<PropertyData>("floors", t("main.floors"), (data) => data.floors?.value, "trending_up", true);
  totalParkingSpots = new FirebaseDataNumberField<PropertyData>(
    "totalParkingSpots",
    t("main.totalParkingSpots"),
    (data) => data.totalParkingSpots?.value,
    "local_parking"
  );
  constructionYear = new FirebaseDataStringField<PropertyData>(
    "constructionYear",
    t("main.constructionYear"),
    (data) => data.constructionYear?.value,
    "crop_free"
  );

  plotArea = new FirebaseDataNumberField<PropertyData>("plotArea", t("main.plotArea"), (data) => data.plotArea?.value, "crop_free", false, {
    metric: "sqm",
    decimals: 1,
  });
  totalArea = new FirebaseDataNumberField<PropertyData>("totalArea", t("main.totalArea"), (data) => data.totalArea?.value, "crop_free", false, {
    metric: "sqm",
    decimals: 1,
  });
  usageArea = new FirebaseDataNumberField<PropertyData>("usageArea", t("main.usageArea"), (data) => data.usageArea?.value, "crop_free", false, {
    metric: "sqm",
    decimals: 1,
  });
  builtArea = new FirebaseDataNumberField<PropertyData>("builtArea", t("main.builtArea"), (data) => data.builtArea?.value, "crop_free", false, {
    metric: "sqm",
    decimals: 1,
  });
  netArea = new FirebaseDataNumberField<PropertyData>("netArea", t("main.netArea"), (data) => data.netArea?.value, "crop_free", false, {
    metric: "sqm",
    decimals: 1,
  });
  primaryArea = new FirebaseDataNumberField<PropertyData>(
    "primaryArea",
    t("main.primaryArea"),
    (data) => data.primaryArea?.value,
    "crop_free",
    false,
    { metric: "sqm", decimals: 1 }
  );
  secondaryArea = new FirebaseDataNumberField<PropertyData>(
    "secondaryArea",
    t("main.secondaryArea"),
    (data) => data.secondaryArea?.value,
    "crop_free",
    false,
    { metric: "sqm", decimals: 1 }
  );
  rentableArea = new FirebaseDataNumberField<PropertyData>(
    "rentableArea",
    t("main.rentableArea"),
    (data) => data.rentableArea?.value,
    "crop_free",
    false,
    { metric: "sqm", decimals: 1 }
  );
  jointArea = new FirebaseDataNumberField<PropertyData>("jointArea", t("main.jointArea"), (data) => data.jointArea?.value, "crop_free", false, {
    metric: "sqm",
    decimals: 1,
  });
  ownedArea = new FirebaseDataNumberField<PropertyData>("ownedArea", t("main.ownedArea"), (data) => data.ownedArea?.value, "crop_free", false, {
    metric: "sqm",
    decimals: 1,
  });
  rentedArea = new FirebaseDataNumberField<PropertyData>("rentedArea", t("main.rentedArea"), (data) => data.rentedArea?.value, "crop_free", false, {
    metric: "sqm",
    decimals: 1,
  });
  heatedArea = new FirebaseDataNumberField<PropertyData>(
    "heatedArea",
    t("main.constructionArea"),
    (data) => data.heatedArea?.value,
    "crop_free",
    false,
    { metric: "sqm", decimals: 1 }
  );

  buildingOwner = new FirebaseDataStringField<PropertyData>("buildingOwner", t("main.buildingOwner"), (data) => data.buildingOwner?.value, "person");
  architects = new FirebaseDataStringField<PropertyData>("architects", t("main.architects"), (data) => data.architects?.value, "place");
  managementCompanies = new FirebaseDataStringField<PropertyData>(
    "managementCompanies",
    t("main.managementCompanies"),
    (data) => data.managementCompanies?.value,
    "place"
  );
  insuranceCompanies = new FirebaseDataStringField<PropertyData>(
    "insuranceCompanies",
    t("main.insuranceCompanies"),
    (data) => data.insuranceCompanies?.value,
    "place"
  );
  fireSafetyResponsibles = new FirebaseDataUserField<PropertyData>(
    "fireSafetyResponsibles",
    t("main.fireSafetyResponsibles"),
    (data) => data.fireSafetyResponsibles?.value,
    "place",
    true
  );

  riskClass = new FirebaseDataStringField<PropertyData>("riskClass", t("main.riskClass"), (data) => data.riskClass?.value, "crop_free");
  fireClass = new FirebaseDataStringField<PropertyData>("fireClass", t("main.fireClass"), (data) => data.fireClass?.value, "crop_free");
  energyScore = new FirebaseDataStringField<PropertyData>("energyScore", t("main.energyScore"), (data) => data.energyScore?.value, "score");
  heatingScore = new FirebaseDataStringField<PropertyData>("heatingScore", t("main.heatingScore"), (data) => data.heatingScore?.value, "score");
  protectionClass = new FirebaseDataStringField<PropertyData>(
    "protectionClass",
    t("main.protectionClass"),
    (data) => data.protectionClass?.value,
    "score"
  );
  maxPeopleAllowed = new FirebaseDataNumberField<PropertyData>(
    "maxPeopleAllowed",
    t("main.maxPeopleAllowed"),
    (data) => data.maxPeopleAllowed?.value,
    "crop_free"
  );

  jointCost = new PropertyDataJointCostField<PropertyData>("jointCost", t("main.jointCost"), (data) => data.jointCost, "percent", false);
  totalJointCost = new FirebaseDataNumberField<PropertyData>(
    "totalJointCost",
    t("main.totalJointCost"),
    (data) => data.totalJointCost?.value,
    "attach_money",
    false,
    { metric: "currency", decimals: 0 }
  );
  marketValue = new FirebaseDataNumberField<PropertyData>(
    "marketValue",
    t("main.marketValue"),
    (data) => data.marketValue?.value,
    "attach_money",
    false,
    { metric: "currency", decimals: 0 }
  );
  balanceSheetValue = new FirebaseDataNumberField<PropertyData>(
    "balanceSheetValue",
    t("main.balanceSheetValue"),
    (data) => data.balanceSheetValue?.value,
    "attach_money",
    false,
    { metric: "currency", decimals: 0 }
  );
  grossYield = new FirebaseDataNumberField("grossYield", t("main.grossYield") + " (%)", (data) => data.grossYield?.value, "attach_money", false, {
    metric: "percentage",
  });
  purchaseDate = new FirebaseDataDateField<PropertyData>("purchaseDate", t("main.purchaseDate"), (data) => data.purchaseDate?.value, "clock");
  purchaseSum = new FirebaseDataNumberField<PropertyData>(
    "purchaseSum",
    t("main.purchaseSum"),
    (data) => data.purchaseSum?.value,
    "attach_money",
    false,
    { metric: "currency", decimals: 0 }
  );
  initialLoan = new FirebaseDataNumberField<PropertyData>(
    "initialLoan",
    t("main.initialLoan"),
    (data) => data.initialLoan?.value,
    "attach_money",
    false,
    { metric: "currency", decimals: 0 }
  );
  remainingLoan = new FirebaseDataNumberField<PropertyData>(
    "remainingLoan",
    t("main.remainingLoan"),
    (data) => data.remainingLoan?.value,
    "attach_money",
    false,
    { metric: "currency", decimals: 0 }
  );

  wault = new FirebaseDataNumberField<PropertyData>(
    "wault",
    t("main.wault"),
    (data) => ExpirationProfileWidgetHelper.getWault([data.units], new Date().getFullYear()),
    "attach_money",
    false,
    { metric: "years" }
  );
  rentedAreaPercentage = new FirebaseDataNumberField<PropertyData>(
    "rentedAreaPercentage",
    t("main.rentedAreaPercentage"),
    (data) => data.getRentedArea() / data.getRentableArea(),
    "crop_free",
    false,
    { metric: "percentage" }
  );
  pricePerArea = new FirebaseDataNumberField<PropertyData>(
    "pricePerArea",
    t("main.pricePerArea"),
    (data) => data.getPricePerArea(),
    "crop_free",
    false,
    { metric: "currency", decimals: 1 }
  );

  note = new FirebaseDataStringField<PropertyData>("note", t("main.note"), (data) => data.note?.value, "notes", false, true);

  // calculation
  loanToValue = new FirebaseDataNumberField<PropertyData>("loanToValue", "LTV", (data) => data.getLoanToValue(), "attach_money", false, {
    metric: "percentage",
  });
  vatKey = new FirebaseDataNumberField<PropertyData>("vatKey", t("main.vatKey"), (data) => data.getVatKey(), "attach_money", false, {
    metric: "percentage",
  });

  override getAllDatas(): PropertyData[] {
    return LocalData.currentUser.properties;
  }

  getInfoFields() {
    return [
      this.location,
      this.address,
      this.visitorAddress,
      this.postalAddress,
      this.city,
      this.municipality,
      this.municipalityNumber,
      this.organizationName,
      this.organizationNumber,
      this.gnr,
      this.bnr,
      this.snr,
      this.floors,
      this.totalParkingSpots,
      this.constructionYear,
      this.plotArea,
      this.totalArea,
      this.usageArea,
      this.builtArea,
      this.netArea,
      this.primaryArea,
      this.secondaryArea,
      this.rentableArea,
      this.jointArea,
      this.ownedArea,
      this.rentedArea,
      this.heatedArea,
      this.buildingOwner,
      this.architects,
      this.managementCompanies,
      this.insuranceCompanies,
      this.fireSafetyResponsibles,
      this.riskClass,
      this.fireClass,
      this.energyScore,
      this.heatingScore,
      this.protectionClass,
      this.maxPeopleAllowed,
      this.jointCost,
      this.totalJointCost,
      this.marketValue,
      this.balanceSheetValue,
      this.grossYield,
      this.purchaseDate,
      this.purchaseSum,
      this.initialLoan,
      this.remainingLoan,
      this.note,
    ];
  }

  getICStandardFields() {
    return [
      this.address,
      this.gnr,
      this.bnr,
      this.snr,
      this.floors,
      this.constructionYear,
      this.totalArea,
      this.buildingOwner,
      this.architects,
      this.managementCompanies,
      this.insuranceCompanies,
      this.energyScore,
      this.heatingScore,
    ];
  }

  getICFireFields() {
    return [this.riskClass, this.fireClass, this.protectionClass, this.maxPeopleAllowed];
  }
  override allFields() {
    return [
      this.name,
      this.companies,
      this.contractorGroups,
      ...this.getInfoFields(),
      this.loanToValue,
      this.vatKey,
      this.wault,
      this.rentedAreaPercentage,
      this.pricePerArea,
    ];
  }

  getAllDataTableDatas(documents: PropertyData[]): DataTableData[] {
    return documents.map((property) => {
      function getElement(getter: () => FirebaseInfoField<any> | undefined) {
        if (!property.infoFieldsLoaded)
          return new Promise<DataTableDataCellValue>((resolve) => {
            property.loadInfoFields(() => {
              const field = getter();
              resolve(field ? field.value + (field.suffix ? " " + field.suffix : "") : "");
            });
          });
        const field = getter();
        return field ? field.value + (field.suffix ? " " + field.suffix : "") : "";
      }

      return {
        key: property.id,
        cells: {
          name: property.name,
          location: getElement(() => property.location),
          address: getElement(() => property.address),
          visitorAddress: getElement(() => property.visitorAddress),
          postalAddress: getElement(() => property.postalAddress),
          city: getElement(() => property.city),
          municipality: getElement(() => property.municipality),
          municipalityNumber: getElement(() => property.municipalityNumber),
          organizationName: getElement(() => property.organizationName),
          organizationNumber: getElement(() => property.organizationNumber),
          gnr: getElement(() => property.gnr),
          bnr: getElement(() => property.bnr),
          snr: getElement(() => property.snr),
          floors: getElement(() => property.floors),
          totalParkingSpots: getElement(() => property.totalParkingSpots),
          constructionYear: getElement(() => property.constructionYear),
          plotArea: getElement(() => property.plotArea),
          totalArea: getElement(() => property.totalArea),
          usageArea: getElement(() => property.usageArea),
          builtArea: getElement(() => property.builtArea),
          netArea: getElement(() => property.netArea),
          primaryArea: getElement(() => property.primaryArea),
          secondaryArea: getElement(() => property.secondaryArea),
          rentableArea: getElement(() => property.rentableArea),
          jointArea: getElement(() => property.jointArea),
          ownedArea: getElement(() => property.ownedArea),
          rentedArea: getElement(() => property.rentedArea),
          heatedArea: getElement(() => property.heatedArea),
          buildingOwner: getElement(() => property.buildingOwner),
          architects: getElement(() => property.architects),
          managementCompanies: getElement(() => property.managementCompanies),
          insuranceCompanies: getElement(() => property.insuranceCompanies),
          fireSafetyResponsibles: getElement(() => property.fireSafetyResponsibles),
          riskClass: getElement(() => property.riskClass),
          fireClass: getElement(() => property.fireClass),
          energyScore: getElement(() => property.energyScore),
          heatingScore: getElement(() => property.heatingScore),
          protectionClass: getElement(() => property.protectionClass),
          maxPeopleAllowed: getElement(() => property.maxPeopleAllowed),
          jointCost: getElement(() => property.jointCost),
          totalJointCost: getElement(() => property.totalJointCost),
          marketValue: getElement(() => property.marketValue),
          balanceSheetValue: getElement(() => property.balanceSheetValue),
          grossYield: getElement(() => property.grossYield),
          purchaseDate: getElement(() => property.purchaseDate),
          purchaseSum: getElement(() => property.purchaseSum),
          initialLoan: getElement(() => property.initialLoan),
          remainingLoan: getElement(() => property.remainingLoan),
          note: getElement(() => property.note),
        },
      };
    });
  }

  loadAll(): Promise<any> {
    return Promise.all(
      LocalData.currentUser.properties.map(
        (p) =>
          new Promise((resolve) => {
            p.loadInfoFields(() => resolve(p));
          })
      )
    );
  }

  areAllLoaded(): boolean {
    return LocalData.currentUser.properties.every((p) => p.infoFieldsLoaded);
  }

  override saveUpdate(data: PropertyData, updatedRawData: { [key: string]: any }): Promise<any> {
    const fields: { [key: string]: { value: any } } = {};
    for (const key of Object.keys(updatedRawData)) {
      fields[key] = { value: updatedRawData[key] };
    }

    return httpsCallable(
      getFunctions(undefined, "europe-west1"),
      "updatePropertyInfoFields"
    )({
      propertyID: data.id,
      fields: fields,
    }).catch((error) => {
      ToastUtilities.presentServerError(error);
    });
  }
}

